// Libs
import React, { useRef, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { IoDocumentTextOutline } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { filesize } from "filesize";

export default function DocumentUpload({
  files,
  setFiles,
}: {
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [, setIsDragging] = useState(false);

  // Utility function to merge files and avoid duplicates (by name and size)
  function mergeFiles(existing: File[], incoming: File[]): File[] {
    const existingMap = new Map<string, File>();

    existing.forEach((f) => existingMap.set(f.name + f.size, f));
    incoming.forEach((f) => {
      // Add new file only if it doesn't exist by name+size
      if (!existingMap.has(f.name + f.size))
        existingMap.set(f.name + f.size, f);
    });

    return Array.from(existingMap.values());
  }

  // Handle drag events to style the div during drag-over
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Handle the file drop on the div
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles((prevFiles) => mergeFiles(prevFiles, newFiles)); // merge new files to existing
      e.dataTransfer.clearData();
    }
  };

  // Handle file selection from the hidden input (e.g., when user clicks div)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles((prevFiles) => mergeFiles(prevFiles, newFiles)); // merge new files to existing
      e.target.value = "";
    }
  };

  // Remove file handler (called on cancel button click)
  const handleRemoveFile = (fileToRemove: File) => {
    setFiles((prevFiles) =>
      prevFiles.filter(
        (f) => !(f.name === fileToRemove.name && f.size === fileToRemove.size)
      )
    );
  };

  // Optionally, a function to open the hidden input via button click
  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      {files.length > 0 && (
        <div className="bg-white p-2 rounded border border-primary">
          {files.map((file, key) => {
            return (
              <div
                className="flex flex-col md:grid md:grid-cols-12 gap-2 md:gap-4 my-3 items-center"
                key={key}
              >
                <div className="flex w-full md:col-span-3">
                  <div className="flex w-full justify-center md:grid grid-cols-3 gap-2 items-center">
                    <IoDocumentTextOutline
                      size={25}
                      className="text-disable place-self-end"
                    />
                    <span className="text-xs col-span-2 break-words whitespace-normal">
                      {file.name}
                    </span>
                  </div>
                </div>
                <div className="flex w-1/2 md:w-full md:col-span-7">
                  <div className="flex justify-center md:justify-start items-center w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-dashboard h-2 rounded-full w-full"></div>
                  </div>
                </div>
                <div className="flex w-1/2 md:w-full md:col-span-2">
                  <div className="flex w-full justify-center md:grid grid-cols-2 gap-4 items-center">
                    <span className="col-span-1 font-light text-xs text-primary place-self-center">
                      {filesize(file.size, { standard: "jedec" })}
                    </span>
                    <div
                      className="text-xs text-disable cursor-pointer text-center"
                      onClick={() => handleRemoveFile(file)}
                    >
                      <RxCross2
                        className="border-[1.5px] rounded-full"
                        size={20}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div
        className={`border-2 p-2 border-dashed h-14 rounded text-center place-content-center text-sm font-medium relative cursor-pointer ${
          files.length > 0 ? "border-t-transparent -mt-1" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />
        <div
          className="inline-block rounded-full p-2 bg-dashboard text-white absolute -top-1/3"
          style={{ justifySelf: "anchor-center" }}
        >
          <FaPlus size={15} />
        </div>
        <span>
          Drag files here or <span className="text-primary">browse</span>{" "}
        </span>
      </div>
    </>
  );
}
